import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Document, Page, pdfjs } from "react-pdf";
import "react-pdf/dist/esm/Page/AnnotationLayer.css";
import "react-pdf/dist/esm/Page/TextLayer.css";
import moment from "moment/moment";
import { ENTITY_TYPE_SELLER_OUTLET } from "utils/constants/others";
import {
  AlertTriangle,
  CalendarDays,
  CheckCircle2,
  ChevronLeft,
  ChevronRight,
  Download,
  FileText,
  MapPin,
  PhoneCall,
  Share2,
  Trash2,
} from "lucide-react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { STATUS_BEGIN, STATUS_DONE, STATUS_FAIL } from "utils/constants/others";
import { isImageUrl, isPdfUrl } from "utils/pdfUtils";
import { DownloadInvoice } from "operations/operations";
import {
  formatInvoiceAmount,
  formatShippingNoteNumber,
  formatWeight,
  formatWeightValue,
} from "./planShipmentUtils";
import {
  formatShippingNoteSlot,
  formatShippingNoteStatus,
  getShippingNoteDocument,
  getShippingNoteMapUrl,
  hasInvoiceGrn,
  isFulfilledShippingNote,
  isPartiallyFulfilledShippingNote,
} from "./planShipmentShippingNotesUtils";
import usePlanShipmentStore from "./usePlanShipmentStore";
import {
  DetailPageFrame,
  DetailPageShell,
  DetailScrollArea,
} from "./styles.scheduleGroupDetails";
import {
  EmptyState,
  ErrorMessage,
  RetryButton,
  SkeletonCard,
  SkeletonLine,
  StateTitle,
} from "./styles.planShipment";
import {
  ASNBottomActions,
  ASNDetailContent,
  ASNDetailHeader,
  ASNDetailStatus,
  ASNHeaderActions,
  ASNHeaderButton,
  ASNHeaderTitle,
  ASNInvoiceCopy,
  ASNInvoiceList,
  ASNInvoiceRow,
  ASNModalBackdrop,
  ASNModalBody,
  ASNModalCard,
  ASNModalDivider,
  ASNModalError,
  ASNModalIcon,
  ASNModalPrimaryButton,
  ASNModalTextButton,
  ASNModalTitle,
  ASNPoCard,
  ASNPoList,
  ASNPoSubtitle,
  ASNPoTitle,
  ASNSectionTitle,
  CancelShippingNoteButton,
  DeliveryCallButton,
  DeliveryScheduleCard,
  DeliverySummaryCopy,
  DeliverySummaryFooter,
  DocumentViewButton,
  EditShippingNoteButton,
  InvoiceAction,
  InvoiceDocumentButton,
  InvoiceDocumentFallback,
  InvoiceSectionHeading,
  LocationButton,
  NoGrnBadge,
  ScheduleCalendar,
  ScheduleCopy,
  ShipmentSummaryCard,
  ShipmentSummaryRows,
} from "./styles.advanceShippingNoteDetails";
import { Spinner } from "./styles.shippingNoteDetails";

const formatCount = (value) => formatWeightValue(value) || "0";

const formatDetailDate = (value) => {
  const date = moment(value);
  return value && date.isValid() ? date.format("D MMM YYYY") : "—";
};

const resolveDocumentUrl = (documentPath) => {
  try {
    return new URL(documentPath, window.location.origin).href;
  } catch (_) {
    return documentPath;
  }
};

const getInvoiceDocumentPath = (invoice = {}) =>
  invoice.invoice_path || invoice.invoice_image_url || "";

pdfjs.GlobalWorkerOptions.workerSrc = `https://cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.js`;

const AsnPdfThumbnail = ({ url }) => {
  const [loadError, setLoadError] = useState(false);

  if (loadError) {
    return (
      <InvoiceDocumentFallback>
        <FileText aria-hidden="true" />
      </InvoiceDocumentFallback>
    );
  }

  return (
    <Document
      file={url}
      loading={null}
      onLoadError={() => setLoadError(true)}
      onSourceError={() => setLoadError(true)}
    >
      <Page
        pageNumber={1}
        width={58}
        renderAnnotationLayer={false}
        renderTextLayer={false}
        loading={null}
        onRenderError={() => setLoadError(true)}
      />
    </Document>
  );
};

const getDeliveryTypeLabel = (deliveryType) => {
  const normalizedDeliveryType = String(deliveryType || "").toLowerCase();
  if (normalizedDeliveryType === "courier") return "Courier / Part load";
  if (normalizedDeliveryType === "direct") return "Direct Vehicle";
  return "—";
};

const ShippingNoteDetailSkeleton = () => (
  <ASNDetailContent aria-label="Loading shipping-note details">
    <SkeletonCard>
      <SkeletonLine $height="72px" />
    </SkeletonCard>
    <SkeletonLine $width="44%" $height="18px" />
    <SkeletonCard>
      {[0, 1, 2, 3, 4, 5].map((item) => (
        <SkeletonLine key={item} $height="16px" />
      ))}
    </SkeletonCard>
    <SkeletonLine $width="58%" $height="18px" />
    <SkeletonCard>
      <SkeletonLine $height="20px" />
      <SkeletonLine $height="14px" />
      <SkeletonLine $height="58px" />
      <SkeletonLine $height="58px" />
    </SkeletonCard>
  </ASNDetailContent>
);

const SummaryRow = ({ label, children }) => (
  <>
    <dt>{label}</dt>
    <dd>{children}</dd>
  </>
);

const SummaryDocument = ({ document, label, onOpenDocument }) =>
  document?.document_path ? (
    <DocumentViewButton
      type="button"
      onClick={() =>
        onOpenDocument(
          document.document_path,
          document.document_number || label
        )
      }
    >
      {label}
      <FileText aria-hidden="true" />
    </DocumentViewButton>
  ) : (
    "—"
  );

export const ASNDeliverySummary = ({ shippingNote, onOpenDocument }) => {
  const deliveryDetails = shippingNote?.delivery_details || {};
  const deliveryType = String(
    deliveryDetails.delivery_type || ""
  ).toLowerCase();
  const isCourier = deliveryType === "courier";
  const ewayBillDocument = getShippingNoteDocument(shippingNote, "eway");
  const courierDocument = getShippingNoteDocument(shippingNote, "courier");
  const phoneNumber = String(deliveryDetails.da_identifier_value || "").trim();
  const callablePhoneNumber = phoneNumber.replace(/[^+\d]/g, "");
  const purchaseOrders = Array.isArray(shippingNote?.purchase_orders)
    ? shippingNote.purchase_orders
    : [];
  const purchaseOrderCount =
    Number(shippingNote?.purchase_order_count) || purchaseOrders.length;

  return (
    <ShipmentSummaryCard>
      <ShipmentSummaryRows>
        <SummaryRow label="Invoices">
          {formatCount(shippingNote?.invoice_count)}
        </SummaryRow>
        <SummaryRow label="POs">{formatCount(purchaseOrderCount)}</SummaryRow>
        <SummaryRow label="Net weight">
          {formatWeight(shippingNote?.total_weight_in_kg)}
        </SummaryRow>
        <SummaryRow label="Total amount">
          {formatInvoiceAmount(shippingNote?.total_amount)}
        </SummaryRow>
        <SummaryRow label="Delivery type">
          {getDeliveryTypeLabel(deliveryType)}
        </SummaryRow>
        {isCourier ? (
          <SummaryRow label="Docket receipt">
            <SummaryDocument
              document={courierDocument}
              label="View receipt"
              onOpenDocument={onOpenDocument}
            />
          </SummaryRow>
        ) : null}
        <SummaryRow label="E way bill">
          <SummaryDocument
            document={ewayBillDocument}
            label="View bill"
            onOpenDocument={onOpenDocument}
          />
        </SummaryRow>
        <SummaryRow label="E way bill no.">
          {ewayBillDocument?.document_number || "—"}
        </SummaryRow>
      </ShipmentSummaryRows>

      <DeliverySummaryFooter>
        <DeliverySummaryCopy>
          <strong>
            {isCourier
              ? deliveryDetails.da_name || "—"
              : deliveryDetails.vehicle_number || "—"}
          </strong>
          <span>
            {isCourier
              ? courierDocument?.document_number || "—"
              : deliveryDetails.da_name || "—"}
          </span>
        </DeliverySummaryCopy>
        {!isCourier && callablePhoneNumber ? (
          <DeliveryCallButton
            href={`tel:${callablePhoneNumber}`}
            aria-label={`Call ${deliveryDetails.da_name || "driver"}`}
          >
            <PhoneCall aria-hidden="true" />
          </DeliveryCallButton>
        ) : null}
      </DeliverySummaryFooter>
    </ShipmentSummaryCard>
  );
};

const InvoiceThumbnail = ({ invoice, onOpenDocument }) => {
  const documentPath = getInvoiceDocumentPath(invoice);
  const resolvedUrl = documentPath ? resolveDocumentUrl(documentPath) : "";
  const canOpen = Boolean(documentPath);
  const showImage = isImageUrl(documentPath) && resolvedUrl;
  const showPdf = !showImage && isPdfUrl(documentPath) && resolvedUrl;

  return (
    <InvoiceDocumentButton
      type="button"
      disabled={!canOpen}
      $clickable={canOpen}
      onClick={() =>
        canOpen && onOpenDocument(documentPath, invoice.invoice_number)
      }
      aria-label={
        canOpen
          ? `Open invoice ${invoice.invoice_number || "document"}`
          : undefined
      }
    >
      {showImage ? (
        <img src={resolvedUrl} alt="" />
      ) : showPdf ? (
        <AsnPdfThumbnail url={resolvedUrl} />
      ) : (
        <InvoiceDocumentFallback>
          <FileText aria-hidden="true" />
        </InvoiceDocumentFallback>
      )}
    </InvoiceDocumentButton>
  );
};

export const ASNInvoiceDetailRow = ({
  invoice,
  purchaseOrderId,
  shippingNoteStatus,
  onOpenDocument,
  onOpenGrn,
}) => {
  const hasGrn = hasInvoiceGrn(invoice);
  const showNoGrn =
    !hasGrn && isPartiallyFulfilledShippingNote(shippingNoteStatus);

  return (
    <ASNInvoiceRow>
      <InvoiceThumbnail invoice={invoice} onOpenDocument={onOpenDocument} />
      <ASNInvoiceCopy>
        <strong>{invoice?.invoice_number || "—"}</strong>
        <span>
          {hasGrn
            ? invoice?.grn_number || "GRN available"
            : `${formatCount(invoice?.number_of_skus)} SKUs • ${formatWeight(
                invoice?.weight_in_kg
              )} • ${formatInvoiceAmount(invoice?.invoice_amount)}`}
        </span>
      </ASNInvoiceCopy>
      {hasGrn ? (
        <InvoiceAction
          type="button"
          onClick={() => onOpenGrn(purchaseOrderId, invoice.po_grn_mapping_id)}
          aria-label={`Open GRN ${
            invoice.grn_number || invoice.po_grn_mapping_id
          }`}
        >
          <ChevronRight aria-hidden="true" />
        </InvoiceAction>
      ) : showNoGrn ? (
        <NoGrnBadge>No GRN</NoGrnBadge>
      ) : null}
    </ASNInvoiceRow>
  );
};

export const ASNPurchaseOrderCard = ({
  purchaseOrder,
  shippingNoteStatus,
  onOpenDocument,
  onOpenGrn,
}) => {
  const invoices = Array.isArray(purchaseOrder?.invoices)
    ? purchaseOrder.invoices
    : [];
  const showGrnHeading = isFulfilledShippingNote(shippingNoteStatus);

  return (
    <ASNPoCard>
      <ASNPoTitle>{purchaseOrder?.purchase_order_number || "—"}</ASNPoTitle>
      <ASNPoSubtitle>
        {formatCount(purchaseOrder?.total_sku_count)} SKUs
        {" • "}
        {formatWeight(purchaseOrder?.total_weight_in_kg)}
        {" • "}
        {formatInvoiceAmount(purchaseOrder?.total_price)}
      </ASNPoSubtitle>
      <InvoiceSectionHeading>
        {showGrnHeading ? "Invoice & GRNs for this PO" : "Invoices for this PO"}
      </InvoiceSectionHeading>
      <ASNInvoiceList>
        {invoices.map((invoice, index) => (
          <ASNInvoiceDetailRow
            key={
              invoice.vendor_invoice_id || `${invoice.invoice_number}-${index}`
            }
            invoice={invoice}
            purchaseOrderId={purchaseOrder.purchase_order_id}
            shippingNoteStatus={shippingNoteStatus}
            onOpenDocument={onOpenDocument}
            onOpenGrn={onOpenGrn}
          />
        ))}
      </ASNInvoiceList>
    </ASNPoCard>
  );
};

const AdvanceShippingNoteDetails = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { asnId } = useParams();
  const parsedAsnId = Number(asnId);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const {
    getAdvanceShippingNoteDetails,
    getAdvanceShippingNoteDetailsStatus,
    advanceShippingNoteDetails,
    advanceShippingNoteDetailsError,
    advanceShippingNotes,
    setShippingNoteSelection,
    deleteAdvanceShippingNote,
    deleteAdvanceShippingNoteStatus,
    deleteAdvanceShippingNoteError,
    resetDeleteAdvanceShippingNote,
    planShipmentHelpSections,
    planShipmentAsnConfig,
  } = usePlanShipmentStore();
  const vendorId = window.localStorage.getItem("outlet_id");
  const hasValidVendorId =
    Number.isInteger(Number(vendorId)) && Number(vendorId) > 0;
  const hasValidAsnId = Number.isInteger(parsedAsnId) && parsedAsnId > 0;
  const cachedShippingNote = useMemo(
    () =>
      (Array.isArray(advanceShippingNotes) ? advanceShippingNotes : []).find(
        (shippingNote) => Number(shippingNote?.asn_id) === parsedAsnId
      ),
    [advanceShippingNotes, parsedAsnId]
  );

  const fetchShippingNoteDetails = useCallback(() => {
    if (!hasValidVendorId || !hasValidAsnId) return;

    getAdvanceShippingNoteDetails({
      entity_type: ENTITY_TYPE_SELLER_OUTLET,
      entity_id: vendorId,
      asn_id: parsedAsnId,
    });
  }, [
    getAdvanceShippingNoteDetails,
    hasValidAsnId,
    hasValidVendorId,
    parsedAsnId,
    vendorId,
  ]);

  useEffect(() => {
    fetchShippingNoteDetails();
  }, [fetchShippingNoteDetails]);

  const shippingNoteNumber = formatShippingNoteNumber({
    ...(advanceShippingNoteDetails || {}),
    asn_id: parsedAsnId,
    asn_number:
      advanceShippingNoteDetails?.asn_number ||
      location.state?.asnNumber ||
      cachedShippingNote?.asn_number,
  });
  const status =
    advanceShippingNoteDetails?.status || cachedShippingNote?.status;
  const mapUrl = getShippingNoteMapUrl(advanceShippingNoteDetails || {});
  const asnDocument = getShippingNoteDocument(
    advanceShippingNoteDetails || {},
    "asn"
  );
  const purchaseOrders = Array.isArray(
    advanceShippingNoteDetails?.purchase_orders
  )
    ? advanceShippingNoteDetails.purchase_orders
    : [];
  const isLoading =
    getAdvanceShippingNoteDetailsStatus === STATUS_BEGIN ||
    !getAdvanceShippingNoteDetailsStatus;

  const openDocument = useCallback(
    (documentPath, title, showDownload = false) => {
      if (!documentPath) return;
      const documentUrl = resolveDocumentUrl(documentPath);

      if (
        window.localStorage.getItem("referrer_platform") === "app" &&
        window.openDocumentHandler
      ) {
        window.openDocumentHandler.postMessage(
          JSON.stringify({ url: documentUrl, title, showDownload })
        );
        return;
      }

      window.open(documentUrl, "_blank", "noopener,noreferrer");
    },
    []
  );

  const downloadShippingNote = () => {
    if (!asnDocument?.document_path) return;

    if (window.localStorage.getItem("referrer_platform") === "app") {
      openDocument(asnDocument.document_path, shippingNoteNumber, true);
      return;
    }

    DownloadInvoice(
      resolveDocumentUrl(asnDocument.document_path),
      shippingNoteNumber
    );
  };

  const shareShippingNote = async () => {
    const documentUrl = asnDocument?.document_path
      ? resolveDocumentUrl(asnDocument.document_path)
      : "";

    if (documentUrl) {
      try {
        const response = await fetch(documentUrl);
        const blob = await response.blob();
        const file = new File([blob], `${shippingNoteNumber}.pdf`, {
          type: "application/pdf",
        });

        if (navigator.canShare?.({ files: [file] })) {
          await navigator.share({
            title: shippingNoteNumber,
            text: `Shipping note ${shippingNoteNumber}`,
            files: [file],
          });
          return;
        }
      } catch (error) {
        if (error?.name === "AbortError") return;
      }
    }

    const shareData = {
      title: shippingNoteNumber,
      text: `Shipping note ${shippingNoteNumber}`,
      ...(documentUrl ? { url: documentUrl } : {}),
    };

    if (navigator.share) {
      try {
        await navigator.share(shareData);
      } catch (error) {
        if (error?.name !== "AbortError") throw error;
      }
      return;
    }

    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(documentUrl || shippingNoteNumber);
    }
  };

  const openGrn = (purchaseOrderId, poGrnMappingId) => {
    if (!(Number(purchaseOrderId) > 0) || !(Number(poGrnMappingId) > 0)) return;
    navigate(`/grn/details/pending/${purchaseOrderId}/${poGrnMappingId}`);
  };

  const editShippingDetails = () => {
    const editScheduleGroupId = Number(
      advanceShippingNoteDetails?.schedule_group_id
    );
    if (!(editScheduleGroupId > 0) || !hasValidAsnId) return;

    const asnInvoiceIds = [];
    purchaseOrders.forEach((po) => {
      (Array.isArray(po.invoices) ? po.invoices : []).forEach((invoice) => {
        const invoiceId = Number(invoice.vendor_invoice_id);
        if (invoiceId > 0) asnInvoiceIds.push(invoiceId);
      });
    });

    setShippingNoteSelection({
      schedule_group_id: editScheduleGroupId,
      vendor_invoice_ids: asnInvoiceIds,
      asn_id: parsedAsnId,
    });
    navigate(`/plan-shipment/schedule-groups/${editScheduleGroupId}`);
  };

  const openDeleteConfirm = () => {
    resetDeleteAdvanceShippingNote();
    setShowDeleteConfirm(true);
  };

  const closeDeleteConfirm = () => {
    setShowDeleteConfirm(false);
    resetDeleteAdvanceShippingNote();
  };

  const confirmDeleteShippingNote = () => {
    if (!hasValidAsnId || deleteAdvanceShippingNoteStatus === STATUS_BEGIN) {
      return;
    }
    deleteAdvanceShippingNote({ asn_id: parsedAsnId }).catch(() => {});
  };

  const finishDeleteShippingNote = () => {
    setShowDeleteConfirm(false);
    resetDeleteAdvanceShippingNote();
    navigate("/plan-shipment", { replace: true });
  };

  return (
    <DetailPageShell>
      <DetailPageFrame>
        <ASNDetailHeader>
          <ASNHeaderButton
            type="button"
            onClick={() =>
              navigate("/plan-shipment", {
                state: { activeTab: "shipping_notes" },
                replace: true,
              })
            }
            aria-label="Go back"
          >
            <ChevronLeft aria-hidden="true" />
          </ASNHeaderButton>
          <ASNHeaderTitle>{shippingNoteNumber}</ASNHeaderTitle>
          <ASNHeaderActions>
            <ASNHeaderButton
              type="button"
              onClick={downloadShippingNote}
              disabled={!asnDocument?.document_path}
              aria-label="Download shipping note"
            >
              <Download aria-hidden="true" />
            </ASNHeaderButton>
            <ASNHeaderButton
              type="button"
              onClick={shareShippingNote}
              aria-label="Share shipping note"
            >
              <Share2 aria-hidden="true" />
            </ASNHeaderButton>
          </ASNHeaderActions>
        </ASNDetailHeader>

        <ASNDetailStatus $status={status}>
          {formatShippingNoteStatus(status)}
        </ASNDetailStatus>

        <DetailScrollArea>
          {(!hasValidVendorId || !hasValidAsnId) && (
            <ASNDetailContent>
              <EmptyState>
                <StateTitle>Unable to load shipping note</StateTitle>
                <ErrorMessage>
                  The outlet or ASN number is invalid.
                </ErrorMessage>
              </EmptyState>
            </ASNDetailContent>
          )}

          {hasValidVendorId && hasValidAsnId && isLoading ? (
            <ShippingNoteDetailSkeleton />
          ) : null}

          {getAdvanceShippingNoteDetailsStatus === STATUS_FAIL ? (
            <ASNDetailContent>
              <EmptyState>
                <StateTitle>Unable to load shipping note</StateTitle>
                <ErrorMessage>{advanceShippingNoteDetailsError}</ErrorMessage>
                <RetryButton type="button" onClick={fetchShippingNoteDetails}>
                  Try again
                </RetryButton>
              </EmptyState>
            </ASNDetailContent>
          ) : null}

          {getAdvanceShippingNoteDetailsStatus === STATUS_DONE &&
          advanceShippingNoteDetails ? (
            <ASNDetailContent>
              <DeliveryScheduleCard>
                <ScheduleCalendar>
                  <CalendarDays aria-hidden="true" />
                </ScheduleCalendar>
                <ScheduleCopy>
                  <strong>
                    {formatDetailDate(advanceShippingNoteDetails.delivery_date)}
                    {" • "}
                    {formatShippingNoteSlot(advanceShippingNoteDetails.slot)}
                  </strong>
                  <span>
                    {advanceShippingNoteDetails.warehouse?.warehouse_name ||
                      advanceShippingNoteDetails.warehouse?.warehouse_code ||
                      "—"}
                  </span>
                </ScheduleCopy>
                <LocationButton
                  href={mapUrl || undefined}
                  target="_blank"
                  rel="noreferrer"
                  $disabled={!mapUrl}
                  aria-label="Open warehouse location in map"
                >
                  <MapPin aria-hidden="true" />
                </LocationButton>
              </DeliveryScheduleCard>

              <ASNSectionTitle>
                <span>Shipment summary</span>
              </ASNSectionTitle>
              <ASNDeliverySummary
                shippingNote={advanceShippingNoteDetails}
                onOpenDocument={openDocument}
              />

              <ASNSectionTitle>
                <span>PO-invoices in this shipment</span>
              </ASNSectionTitle>
              {purchaseOrders.length > 0 ? (
                <ASNPoList>
                  {purchaseOrders.map((purchaseOrder, index) => (
                    <ASNPurchaseOrderCard
                      key={
                        purchaseOrder.purchase_order_id ||
                        `${purchaseOrder.purchase_order_number}-${index}`
                      }
                      purchaseOrder={purchaseOrder}
                      shippingNoteStatus={advanceShippingNoteDetails.status}
                      onOpenDocument={openDocument}
                      onOpenGrn={openGrn}
                    />
                  ))}
                </ASNPoList>
              ) : (
                <EmptyState>
                  <StateTitle>No purchase orders found</StateTitle>
                  <span>This shipping note has no mapped PO invoices.</span>
                </EmptyState>
              )}

              {(() => {
                const deliveryType = String(
                  advanceShippingNoteDetails?.delivery_details?.delivery_type || ""
                ).toLowerCase();
                const helpSectionValue =
                  deliveryType === "courier" ? "COURIER" : "DIRECT_VEHICLE";
                const helpImage =
                  planShipmentHelpSections.find(
                    (section) => section?.value === helpSectionValue
                  )?.cta?.action_data?.image_url || "";
                if (!helpImage) return null;
                return (
                  <>
                    <ASNSectionTitle style={{ marginTop: 28 }}>
                      <span>How it works</span>
                    </ASNSectionTitle>
                    <img
                      src={helpImage}
                      alt="Instructions for driver"
                      style={{
                        display: "block",
                        width: "100%",
                        height: "auto",
                        borderRadius: "12px",
                      }}
                    />
                  </>
                );
              })()}
            </ASNDetailContent>
          ) : null}
        </DetailScrollArea>

        {getAdvanceShippingNoteDetailsStatus === STATUS_DONE &&
        String(status || "").toUpperCase() === "CREATED" ? (() => {
          const deliveryDateStr = advanceShippingNoteDetails?.delivery_date;
          const thresholdMins = Number(planShipmentAsnConfig?.today_time_threshold_for_schedule_edit_in_mins);
          const now = new Date();
          const todayMidnight = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();
          const parsed = deliveryDateStr ? new Date(deliveryDateStr) : null;
          const deliveryMidnight = parsed && !isNaN(parsed.getTime())
            ? new Date(parsed.getFullYear(), parsed.getMonth(), parsed.getDate()).getTime()
            : null;

          const shouldShowEditAsn = (() => {
            if (deliveryMidnight === null) return true;
            if (todayMidnight > deliveryMidnight) return false;
            if (todayMidnight < deliveryMidnight) return true;
            if (!Number.isFinite(thresholdMins) || thresholdMins <= 0) return true;
            const currentMins = now.getHours() * 60 + now.getMinutes();
            return currentMins < thresholdMins;
          })();

          return (
            <ASNBottomActions>
              <CancelShippingNoteButton type="button" onClick={openDeleteConfirm}>
                <Trash2 aria-hidden="true" />
                Cancel ASN
              </CancelShippingNoteButton>
              {shouldShowEditAsn ? (
                <EditShippingNoteButton type="button" onClick={editShippingDetails}>
                  Edit ASN details
                </EditShippingNoteButton>
              ) : null}
            </ASNBottomActions>
          );
        })() : null}
      </DetailPageFrame>

      {showDeleteConfirm ? (
        <ASNModalBackdrop>
          <ASNModalCard
            role="dialog"
            aria-modal="true"
            aria-labelledby="delete-shipping-note-title"
          >
            {deleteAdvanceShippingNoteStatus === STATUS_DONE ? (
              <>
                <ASNModalBody>
                  <ASNModalIcon $tone="success">
                    <CheckCircle2 aria-hidden="true" />
                  </ASNModalIcon>
                  <ASNModalTitle id="delete-shipping-note-title">
                    Shipment deleted
                  </ASNModalTitle>
                </ASNModalBody>
                <ASNModalDivider />
                <ASNModalTextButton
                  type="button"
                  onClick={finishDeleteShippingNote}
                >
                  Okay
                </ASNModalTextButton>
              </>
            ) : (
              <ASNModalBody>
                <ASNModalIcon $tone="warning">
                  <AlertTriangle aria-hidden="true" />
                </ASNModalIcon>
                <ASNModalTitle id="delete-shipping-note-title">
                  Delete {shippingNoteNumber} for{" "}
                  {formatCount(advanceShippingNoteDetails?.invoice_count)}{" "}
                  invoices?
                </ASNModalTitle>
                {deleteAdvanceShippingNoteError ? (
                  <ASNModalError>{deleteAdvanceShippingNoteError}</ASNModalError>
                ) : null}
                <ASNModalPrimaryButton
                  type="button"
                  onClick={confirmDeleteShippingNote}
                  disabled={deleteAdvanceShippingNoteStatus === STATUS_BEGIN}
                >
                  {deleteAdvanceShippingNoteStatus === STATUS_BEGIN ? (
                    <Spinner aria-label="Deleting" />
                  ) : (
                    "Yes, delete"
                  )}
                </ASNModalPrimaryButton>
                <ASNModalTextButton
                  type="button"
                  onClick={closeDeleteConfirm}
                  disabled={deleteAdvanceShippingNoteStatus === STATUS_BEGIN}
                >
                  No
                </ASNModalTextButton>
              </ASNModalBody>
            )}
          </ASNModalCard>
        </ASNModalBackdrop>
      ) : null}
    </DetailPageShell>
  );
};

export default AdvanceShippingNoteDetails;
